"use client";

import React from "react";

interface DRAMADiagramProps {
  activeStep: number;
  onStepClick?: (index: number) => void;
}

/**
 * DRAMA code-review diagram.
 *
 * Faithful port of the live e-cq.net "pentest-map" section (same shapes as the
 * OT-Security AIDICS diagram): each circle is two ring layers (a full gray ring
 * with dots + an arc outline) that slowly rotate in opposite directions on
 * hover, a dark centre disc with the step letter, and an alternating top /
 * bottom label connector (crisp vector fork + stem + dot, plus a two-segment
 * dash). No connecting lines, flat #EC1938, no bloom.
 *
 * The ring angles are v1's, per circle — see the note on that block below for
 * what the odd/even rule they replaced got wrong.
 */

const ASSET = "/images/Service/secure-code-review";

const steps = [
  { letter: "D", name: "Define" },
  { letter: "R", name: "Recon" },
  { letter: "A", name: "Analyze" },
  { letter: "M", name: "Manual Review" },
  { letter: "A", name: "Advise" },
];

const DRAMADiagram: React.FC<DRAMADiagramProps> = ({ onStepClick }) => {
  return (
    <div className="drama-map">
      {steps.map((step, index) => (
        <div
          key={`${step.letter}-${index}`}
          className="drama-item"
          onClick={() => onStepClick?.(index)}
        >
          <div className="drama-wrap">
            <span className="drama-outline" aria-hidden />
            <span className="drama-border" aria-hidden />
            {/* Connector: crisp vector fork + stem + dot. */}
            <svg className="drama-penguin" viewBox="0 0 60 98" fill="none" aria-hidden>
              <path
                d="M2 3 L30 19 L58 3"
                stroke="#EC1938"
                strokeWidth="4.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="30"
                y1="19"
                x2="30"
                y2="76"
                stroke="#EC1938"
                strokeWidth="4.2"
                strokeLinecap="round"
              />
              <circle cx="30" cy="86" r="10" fill="#EC1938" />
            </svg>
            <span className="drama-crash" aria-hidden>
              <i className="drama-crash-long" />
              <i className="drama-crash-short" />
            </span>
            <div className="drama-char">
              <span>{step.letter}</span>
            </div>
          </div>
          <div className="drama-title">{step.name}</div>
        </div>
      ))}

      <style jsx>{`
        .drama-map {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 28px;
          width: 100%;
          max-width: 992px; /* match the design: 5×176px circles + 4×28px gap */
          margin: 0 auto;
          padding: 160px 0;
        }
        .drama-item {
          position: relative;
          width: 100%;
          cursor: pointer;
        }
        .drama-wrap {
          position: relative;
          padding-top: 100%;
        }

        /* Two rotating ring layers */
        .drama-outline,
        .drama-border {
          position: absolute;
          background-position: center;
          background-repeat: no-repeat;
          background-size: contain;
          transition: transform 5s;
        }
        .drama-outline {
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          z-index: 1;
          background-image: url("${ASSET}/pentest-outline.svg");
        }
        .drama-border {
          top: 51%;
          left: 51%;
          width: calc(100% - 30px);
          height: calc(100% - 30px);
          z-index: 2;
          background-image: url("${ASSET}/pentest-border.svg");
        }

        /* Centre disc + letter */
        .drama-char {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 70%;
          height: 70%;
          background: #0d1117;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3;
        }
        .drama-char span {
          font-family: var(--font-avenir), sans-serif;
          font-size: 60px;
          font-weight: 700;
          line-height: 1;
          color: #ec1938;
        }

        /* Label + connector */
        .drama-title {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          color: #ec1938;
          font-family: var(--font-avenir), sans-serif;
          font-size: 24px;
          white-space: nowrap;
          text-align: center;
        }
        .drama-penguin {
          position: absolute;
          width: 40px;
          height: 65px;
          overflow: visible;
        }
        .drama-crash {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .drama-crash-long {
          display: block;
          width: 40px;
          height: 2.8px;
          background: #ec1938;
          border-radius: 2px;
        }
        .drama-crash-short {
          display: block;
          width: 6px;
          height: 2.8px;
          background: #ec1938;
          border-radius: 2px;
        }

        /* Ring angles, taken value for value from v1's own stylesheet
           (assets/css/style.css, .pentest-map__char-item:nth-child(n) in the
           pentest-map block). v1 sets all five circles by hand, and two things
           come out of that which an odd/even rule cannot reproduce:

           - the resting row turns progressively — outline at -90, -36, 0, 90,
             100 degrees — so the five circles read as a sequence rather than as
             two dials alternating;
           - the two layers of a circle rest within ~10deg of each other
             (-90/-80, -36/-45, 0/0, 90/90, 100/90), so each circle reads as one
             assembly. The odd/even pair this replaces left them ~100deg apart,
             which is what made the arcs look scattered.

           Hover keeps v1's pairs too: items 1-3 send the outline to -270 while
           the inner ring goes the other way to +10 (+100 on item 3), and items
           4-5 swing the outline to -180 against an inner -90.

           Item 3 has no resting rule at all in v1 — it just sits on the base
           transform at 0deg. It is written out here anyway so that all five
           resting states can be read in one place.

           Only the rings are keyed per item. Label and connector placement stays
           on odd/even below, which is this component's own arrangement. */
        .drama-item:nth-child(1) .drama-outline {
          transform: translate(-50%, -50%) rotate(-90deg);
        }
        .drama-item:nth-child(1) .drama-border {
          transform: translate(-49%, -53%) rotate(-80deg);
        }
        .drama-item:nth-child(1):hover .drama-outline {
          transform: translate(-50%, -50%) rotate(-270deg);
        }
        .drama-item:nth-child(1):hover .drama-border {
          transform: translate(-50%, -50%) rotate(10deg);
        }

        .drama-item:nth-child(2) .drama-outline {
          transform: translate(-50%, -50%) rotate(-36deg);
        }
        .drama-item:nth-child(2) .drama-border {
          transform: translate(-49%, -51%) rotate(-45deg);
        }
        .drama-item:nth-child(2):hover .drama-outline {
          transform: translate(-50%, -50%) rotate(-270deg);
        }
        .drama-item:nth-child(2):hover .drama-border {
          transform: translate(-50%, -50%) rotate(10deg);
        }

        .drama-item:nth-child(3) .drama-outline {
          transform: translate(-50%, -50%) rotate(0deg);
        }
        .drama-item:nth-child(3) .drama-border {
          transform: translate(-50%, -50%) rotate(0deg);
        }
        .drama-item:nth-child(3):hover .drama-outline {
          transform: translate(-50%, -50%) rotate(-270deg);
        }
        .drama-item:nth-child(3):hover .drama-border {
          transform: translate(-53%, -50%) rotate(100deg);
        }

        .drama-item:nth-child(4) .drama-outline {
          transform: translate(-50%, -50%) rotate(90deg);
        }
        .drama-item:nth-child(4) .drama-border {
          transform: translate(-53%, -50%) rotate(-270deg);
        }
        .drama-item:nth-child(4):hover .drama-outline {
          transform: translate(-50%, -50%) rotate(-180deg);
        }
        .drama-item:nth-child(4):hover .drama-border {
          transform: translate(-49%, -53%) rotate(-90deg);
        }

        .drama-item:nth-child(5) .drama-outline {
          transform: translate(-50%, -50%) rotate(100deg);
        }
        .drama-item:nth-child(5) .drama-border {
          transform: translate(-53%, -50%) rotate(-270deg);
        }
        .drama-item:nth-child(5):hover .drama-outline {
          transform: translate(-50%, -50%) rotate(-180deg);
        }
        .drama-item:nth-child(5):hover .drama-border {
          transform: translate(-49%, -53%) rotate(-90deg);
        }

        /* Odd items (Define / Analyze / Advise) — label above */
        .drama-item:nth-child(odd) .drama-penguin {
          top: -41%;
          left: 50%;
          transform: translateX(-50%);
        }
        .drama-item:nth-child(odd) .drama-title {
          top: -67%;
          left: 50%;
          transform: translateX(-50%);
        }
        .drama-item:nth-child(odd) .drama-crash {
          top: -73%;
          left: 50%;
          transform: translateX(-50%);
        }

        /* Even items (Recon / Manual Review) — label below */
        .drama-item:nth-child(even) .drama-penguin {
          bottom: -41%;
          left: 50%;
          transform: translateX(-50%) rotate(180deg);
        }
        .drama-item:nth-child(even) .drama-title {
          bottom: -67%;
          left: 50%;
          transform: translateX(-50%);
        }
        .drama-item:nth-child(even) .drama-crash {
          bottom: -73%;
          left: 50%;
          transform: translateX(-50%);
        }

        /* 992-1137px: the map is squeezed below its designed 992px, so the
           rotated rings have no slack to hang into and the page scrolls
           sideways (measured 1057px of content in a 1024px viewport). Reserving
           40px a side is a no-op once the column can hold 1072px, so the 992px
           design width and its 176px circles are untouched on a wide desktop.
           Scoped to min-width 992px so it cannot interact with the padding the
           two blocks below apply in the phone and tablet bands. */
        @media (min-width: 992px) {
          .drama-map {
            max-width: min(992px, calc(100% - 80px));
          }
        }

        @media (max-width: 991px) {
          .drama-map {
            /* Same rotated-ring overflow as the 767px block below, which this
               band shares. Measured: 795px of content in a 768px viewport.
               Needed inset is 3.82% of the page content width (solve
               P >= 0.0414 * (C - 2P) for P), so 36px at the 991px top of this
               band; 40px keeps a margin. */
            padding: 70px 40px;
          }
          .drama-char span {
            font-size: 30px;
          }
          .drama-title {
            font-size: 16px;
          }
        }
        @media (max-width: 767px) {
          .drama-map {
            gap: 4px;
            /* 16px of horizontal inset removes the page's only real overflow.
               Both ring layers are square boxes rotated 45deg, so each paints a
               bounding box 1.414x its side and (1.414 - 1) / 2 = 20.7% of the
               cell hangs off each edge. At a 390px viewport the five cells are
               (358 - 16) / 5 = 68.4px, so the outer rings reach 14.2px past the
               content box. Trimming 32px total makes the cells 62px (overhang
               12.8px) and pulls the grid edge 16px inboard: 19.3px less reach.
               Breakpoint note: this component's whole responsive system runs on
               767/991px, and the 640-767px range overflows today too, so fixing
               it here is a strict improvement. Splitting one file across two
               breakpoint systems (767 here, Tailwind's 640 elsewhere) would be
               worse than keeping the existing edge. */
            /* 24px, not 16px: 16px clears a 390px viewport but leaves 6px of
               overflow at the 640px top of this band, where the cells are
               widest. */
            padding: 40px 24px;
          }
          .drama-char span {
            font-size: 15px;
          }
          .drama-title {
            font-size: 12px;
          }
          .drama-penguin,
          .drama-crash {
            display: none;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .drama-outline,
          .drama-border {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

export default DRAMADiagram;
